import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { DiplomaticGroups } from "./DiplomaticGroups";

const searchText = value => value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/* Confidence → colour mapping for the pulse dot */
const CONF_COLOR = {
  High:       "bg-[var(--crimson)]",
  Developing: "bg-amber-500",
  Limited:    "bg-slate-400",
};

export const ExploreDirectory = ({ view, mapData }) => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null), [error, setError] = useState(""), [q, setQ] = useState(""), [limit, setLimit] = useState(60), [retry, setRetry] = useState(0);
  const country = params.get("country") || "", grouped = view === "countries" && params.get("group") === "diplomatic";

  useEffect(() => {
    const controller = new AbortController();
    setError("");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/discover`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setData)
      .catch(() => { if (!controller.signal.aborted) setError("The directory could not be loaded."); });
    return () => controller.abort();
  }, [retry]);

  useEffect(() => { setQ(""); setLimit(60); }, [view, country]);

  const rows = useMemo(() => (data?.[view] || []).filter(row =>
    (view !== "states" || !country || row.country === country) &&
    searchText(`${row.name} ${row.country_name || ""} ${row.iso2 || ""} ${row.code || ""} ${(row.aliases || []).join(" ")}`).includes(searchText(q))
  ), [data, view, q, country]);

  /* Build signal map: country_slug → { count, confidence } */
  const signalMap = useMemo(() => {
    if (!mapData?.points) return {};
    return Object.fromEntries(
      mapData.points.map(p => [p.country_slug, { count: p.count, confidence: p.confidence }])
    );
  }, [mapData]);

  const change = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };

  const totalSignals = mapData?.signals_48h;

  return (
    <div data-testid={`explore-directory-${view}`}>
      {/* Search + filters */}
      <div className="flex items-center gap-3 mb-6 min-w-0">
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setLimit(60); }}
          aria-label={`Find ${view}`}
          placeholder={`Find ${view}`}
          className="dnh-select flex-1 max-w-md w-0"
          data-testid="explore-directory-search"
        />
        {view === "states" && (
          <select
            aria-label="State country"
            value={country}
            onChange={e => change("country", e.target.value)}
            className="dnh-select max-w-[45%]"
            data-testid="explore-state-country"
          >
            <option value="">All countries</option>
            {data?.countries.filter(c => data.states.some(s => s.country === c.slug)).map(c =>
              <option key={c.slug} value={c.slug}>{c.name}</option>
            )}
          </select>
        )}
        {view === "countries" && (
          <select
            aria-label="Country directory view"
            value={grouped ? "diplomatic" : "directory"}
            onChange={e => change("group", e.target.value === "diplomatic" ? "diplomatic" : "")}
            className="dnh-select max-w-[45%]"
            data-testid="explore-country-view"
          >
            <option value="directory">Country directory</option>
            <option value="diplomatic">Diplomatic groupings</option>
          </select>
        )}
      </div>

      {/* Live signal summary strip */}
      {(view === "countries" || view === "states") && totalSignals != null && (
        <div className="flex items-center gap-2 mb-5 font-mono text-[10.5px] text-[var(--muted)] tracking-[0.12em]" data-testid="explore-signal-summary">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)] animate-pulse" />
          <span>
            {mapData.countries_reporting} countries reporting · {totalSignals.toLocaleString()} signals in the last 48h
          </span>
        </div>
      )}

      {error && (
        <p role="alert" data-testid="explore-directory-error">
          {error}
          <button className="dnh-outline-button ml-3" onClick={() => setRetry(n => n + 1)} data-testid="explore-directory-retry">Retry</button>
        </p>
      )}
      {!data && !error && (
        <p className="text-sm text-[var(--muted)]" data-testid="explore-directory-loading">Loading directory…</p>
      )}

      {grouped
        ? <DiplomaticGroups countries={data?.countries || []} query={q} />
        : <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8" data-testid={`explore-${view}-directory`}>
            {rows.slice(0, limit).map(row => {
              const signal = view === "countries"
                ? signalMap[row.slug]
                : view === "states"
                  ? signalMap[row.country]
                  : null;

              return (
                <Link
                  key={row.href}
                  to={row.href}
                  className="flex items-center justify-between gap-3 border-b border-[var(--hairline)] min-h-20 py-4 hover:text-[var(--crimson)] transition-colors group"
                  data-testid={`explore-${view}-${view === "states" ? `${row.iso2}-` : ""}${row.slug}`}
                >
                  <span className="min-w-0">
                    <span className="block font-serif text-lg break-words">{row.name}</span>
                    {row.country_name && (
                      <span className="block text-xs text-[var(--muted)] mt-1" data-testid={`explore-state-parent-${row.iso2}-${row.slug}`}>
                        {row.country_name} · {row.type} · {row.code}
                      </span>
                    )}
                  </span>

                  {/* Right side: signal badge + arrow */}
                  <span className="flex items-center gap-2 shrink-0">
                    {signal && view === "countries" && (
                      <span
                        className="font-mono text-[10px] text-[var(--crimson)] tabular-nums"
                        title={`${signal.count} signals in the last 48h`}
                        data-testid={`explore-signal-count-${row.slug}`}
                      >
                        {signal.count.toLocaleString()}
                      </span>
                    )}
                    {signal && view === "states" && (
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${CONF_COLOR[signal.confidence] || "bg-slate-400"}`}
                        title={`${signal.confidence} activity from ${row.country_name || row.country}`}
                        data-testid={`explore-signal-dot-${row.iso2}-${row.slug}`}
                      />
                    )}
                    <ArrowUpRight size={16} className="shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" />
                  </span>
                </Link>
              );
            })}
          </div>

          {data && !rows.length && (
            <p className="py-8 text-sm text-[var(--muted)]" data-testid="explore-directory-empty">
              No matching {view}.
            </p>
          )}
          {rows.length > limit && (
            <button className="dnh-outline-button mt-6" onClick={() => setLimit(n => n + 60)} data-testid="explore-directory-more">
              More {view}
            </button>
          )}
        </>
      }
    </div>
  );
};
